import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ArrowPathIcon,
  EyeIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { useI18n } from '../i18n/I18nProvider';
import {
  PRACTICE_LABELS,
  fetchPracticeJson,
  fetchTeacherPracticeAssignments,
} from '../lib/practiceAttempts';
import {
  assignmentProgress,
  buildClassPracticeQuery,
  classWideAssignment,
} from '../lib/classPracticeAssignments';
import AdminPracticeAssignmentDetailModal from './AdminPracticeAssignmentDetailModal';

const formatDate = (value, locale) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return new Intl.DateTimeFormat(locale === 'zh-CN' ? 'zh-CN' : 'en', {
    timeZone: 'Asia/Shanghai',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(date);
};

export default function AdminPracticeAssignmentsPanel({
  classId,
  classStudents = [],
  legacyQuizzes = [],
  legacyLoading = false,
  legacyError = '',
  onOpenLegacyQuiz,
  onLegacyQuizDeleted,
}) {
  const { locale, t } = useI18n();
  const [assignments, setAssignments] = useState([]);
  const [classOnly, setClassOnly] = useState(true);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [confirmDeleteKey, setConfirmDeleteKey] = useState('');
  const [deletingKey, setDeletingKey] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const values = await fetchTeacherPracticeAssignments(buildClassPracticeQuery(classId, classOnly));
      setAssignments(values);
    } catch (loadError) {
      setError(loadError.message || t('The practice reports could not be loaded.'));
    } finally {
      setLoading(false);
    }
  }, [classId, classOnly, t]);

  useEffect(() => { load(); }, [load]);

  const rows = useMemo(() => {
    const unifiedRows = assignments.map((assignment) => ({
      id: assignment.id,
      kind: 'practice',
      title: assignment.title,
      practiceType: assignment.practice_type,
      scope: classWideAssignment(assignment) ? 'class' : 'students',
      date: assignment.published_at || assignment.created_at || assignment.updated_at,
      status: assignment.status || 'published',
      progress: assignmentProgress(assignment),
      assignment,
    }));
    const legacyRows = legacyQuizzes.map((quiz) => ({
      id: quiz._id,
      kind: 'legacy',
      title: quiz.name,
      practiceType: 'legacy_quiz',
      scope: 'class',
      date: quiz.publish_at || quiz.created_at,
      status: quiz.status || 'published',
      quiz,
    }));
    return [...unifiedRows, ...legacyRows].sort((left, right) => (
      new Date(right.date || 0).getTime() - new Date(left.date || 0).getTime()
    ));
  }, [assignments, legacyQuizzes]);

  const deleteRow = async (row) => {
    const rowKey = `${row.kind}-${row.id}`;
    setDeletingKey(rowKey);
    setError('');
    try {
      const url = row.kind === 'legacy'
        ? `/api/quizzes/${encodeURIComponent(row.id)}`
        : `/api/admin/practice-assignments/${encodeURIComponent(row.id)}/permanent`;
      await fetchPracticeJson(url, { method: 'DELETE' });
      if (row.kind === 'legacy') {
        onLegacyQuizDeleted?.(row.id);
      } else {
        setAssignments((current) => current.filter((assignment) => assignment.id !== row.id));
        if (selectedAssignment?.id === row.id) setSelectedAssignment(null);
      }
      setConfirmDeleteKey('');
    } catch (deleteError) {
      setError(deleteError.message || t('The practice could not be deleted.'));
    } finally {
      setDeletingKey('');
    }
  };

  return (
    <section className="bg-white p-4 shadow-md">
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-gray-200 pb-4">
        <div>
          <h2 className="text-lg font-bold text-gray-900">{t('Quiz Data')}</h2>
          <p className="mt-1 text-sm text-gray-500">{t('All assigned practices and saved attempts for this class appear here.')}</p>
        </div>
        <div className="flex items-center gap-3">
          <label className="inline-flex cursor-pointer items-center gap-2 text-sm font-semibold text-gray-700">
            <input type="checkbox" checked={classOnly} onChange={(event) => setClassOnly(event.target.checked)} className="h-4 w-4 rounded border-gray-300 text-indigo-600" />
            {t('Only whole-class assignments')}
          </label>
          <button type="button" onClick={load} title={t('Refresh')} aria-label={t('Refresh')} className="rounded border border-gray-300 p-2 text-gray-600 hover:bg-gray-50">
            <ArrowPathIcon className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>
      </div>
      {error && <div className="mt-4 border border-rose-200 bg-rose-50 px-4 py-3 text-rose-800">{error}</div>}
      {legacyError && <div className="mt-4 border border-amber-200 bg-amber-50 px-4 py-3 text-amber-900">{legacyError}</div>}
      {loading && <p className="p-6 text-center text-gray-600">{t('Loading practice reports...')}</p>}
      {!loading && !error && rows.length === 0 && !legacyLoading && <p className="py-8 text-center text-gray-500">{t('No assigned practice reports yet.')}</p>}
      {!loading && rows.length > 0 && (
        <div className="mt-4 overflow-x-auto">
          <table className="min-w-full text-left text-sm">
            <thead className="border-b bg-gray-50 text-xs font-semibold uppercase tracking-wide text-gray-600">
              <tr>
                <th className="px-3 py-3">{t('Practice name')}</th>
                <th className="px-3 py-3">{t('Practice type')}</th>
                <th className="px-3 py-3">{t('Audience')}</th>
                <th className="px-3 py-3">{t('Completion')}</th>
                <th className="px-3 py-3">{t('Published At')}</th>
                <th className="px-3 py-3">{t('Actions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {rows.map((row) => {
                const rowKey = `${row.kind}-${row.id}`;
                const confirmingDelete = confirmDeleteKey === rowKey;
                return (
                  <React.Fragment key={rowKey}>
                    <tr className="hover:bg-gray-50">
                      <td className="px-3 py-3 font-semibold text-gray-950" data-i18n-skip>{row.title || t('Practice')}</td>
                      <td className="px-3 py-3 text-gray-700">{row.kind === 'legacy' ? t('Legacy quiz') : t(PRACTICE_LABELS[row.practiceType] || 'Practice')}</td>
                      <td className="px-3 py-3 text-gray-700">{t(row.scope === 'class' ? 'Whole class' : 'Individual students')}</td>
                      <td className="px-3 py-3 text-gray-700">
                        {row.kind === 'legacy' || !row.progress.total
                          ? t('Open for details')
                          : <span data-i18n-skip>{row.progress.completed}/{row.progress.total}</span>}
                      </td>
                      <td className="px-3 py-3 text-gray-600" data-i18n-skip>{formatDate(row.date, locale)}</td>
                      <td className="px-3 py-3">
                        <div className="flex flex-wrap items-center gap-3">
                          <button type="button" onClick={() => row.kind === 'legacy' ? onOpenLegacyQuiz?.(row.quiz) : setSelectedAssignment(row.assignment)} className="inline-flex items-center gap-2 font-semibold text-indigo-700 hover:underline">
                            <EyeIcon className="h-4 w-4" aria-hidden="true" />
                            {t('View Details')}
                          </button>
                          {!confirmingDelete && (
                            <button
                              type="button"
                              onClick={() => setConfirmDeleteKey(rowKey)}
                              className="inline-flex items-center gap-1 font-semibold text-red-700 hover:underline"
                            >
                              <TrashIcon className="h-4 w-4" aria-hidden="true" />
                              {t('Delete practice')}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                    {confirmingDelete && (
                      <tr className="bg-red-50">
                        <td colSpan={6} className="px-3 py-3">
                          <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-center">
                            <p className="text-sm text-red-800">
                              {t('Delete this practice? All related recordings and attempts will also be permanently deleted. This cannot be undone.')}
                            </p>
                            <div className="flex shrink-0 gap-2">
                              <button
                                type="button"
                                disabled={deletingKey === rowKey}
                                onClick={() => setConfirmDeleteKey('')}
                                className="h-9 rounded border border-gray-300 bg-white px-3 font-semibold text-gray-700 disabled:opacity-50"
                              >
                                {t('Cancel')}
                              </button>
                              <button
                                type="button"
                                disabled={deletingKey === rowKey}
                                onClick={() => deleteRow(row)}
                                className="h-9 rounded bg-red-600 px-3 font-semibold text-white disabled:opacity-50"
                              >
                                {deletingKey === rowKey ? t('Deleting...') : t('Confirm delete')}
                              </button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {selectedAssignment && (
        <AdminPracticeAssignmentDetailModal assignment={selectedAssignment} classStudents={classStudents} onClose={() => setSelectedAssignment(null)} />
      )}
    </section>
  );
}
